// WP-0.7: the app root — providers + router. Standalone routes (setup/login/onboarding)
// render bare; everything else renders inside the AppShell behind an auth gate that
// mirrors the server's default-deny posture (AU-B) and routes first-run to /setup (S-01).
// Split into AppProviders/AppRoutes so the guard flows are integration-testable.
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { AppLockProvider } from './lib/appLock';
import { PrivacyProvider } from './lib/privacy';
import { makeQueryClient } from './lib/queryClient';
import { useAuthStatus } from './lib/session';
import { ThemeProvider } from './lib/theme';
import { AccountDetailPage } from './pages/AccountDetail';
import { AccountsPage } from './pages/Accounts';
import { AdvisorPage } from './pages/Advisor';
import { AgendaPage } from './pages/Agenda';
import { AvailabilityPage } from './pages/Availability';
import { ChatPage } from './pages/Chat';
import { DashboardPage } from './pages/Dashboard';
import { DataHealthPage } from './pages/DataHealth';
import { ExpectedPage } from './pages/Expected';
import { FlowExplorerPage } from './pages/FlowExplorer';
import { GoalsPage } from './pages/Goals';
import { ImportPage } from './pages/Import';
import { InboxPage } from './pages/Inbox';
import { InboxItemPage } from './pages/InboxItem';
import { MoneyIntelligencePage } from './pages/MoneyIntelligence';
import { ReconcilePage } from './pages/Reconcile';
import { LoginPage } from './pages/Login';
import { OnboardingPage } from './pages/Onboarding';
import { OwedPage } from './pages/Owed';
import { PartiesPage } from './pages/Parties';
import { PartyDetailPage } from './pages/PartyDetail';
import { PositionOverTimePage } from './pages/PositionOverTime';
import { ProjectDetailPage } from './pages/ProjectDetail';
import { ProjectsPage } from './pages/Projects';
import { QuotationDetailPage } from './pages/QuotationDetail';
import { QuotationsPage } from './pages/Quotations';
import { RecurringPage } from './pages/Recurring';
import { RemindersPage } from './pages/Reminders';
import { ReportDetailPage } from './pages/ReportDetail';
import { ReportsPage } from './pages/Reports';
import { RouteStub } from './pages/RouteStub';
import { SearchPage } from './pages/Search';
import { SettingsPage } from './pages/Settings';
import { SettingsAbout } from './pages/settings/About';
import { SettingsAi } from './pages/settings/Ai';
import { SettingsAiActivity } from './pages/settings/AiActivity';
import { SettingsBackups } from './pages/settings/Backups';
import { SettingsCalendars } from './pages/settings/Calendars';
import { SettingsFinancial } from './pages/settings/Financial';
import { SettingsNotifications } from './pages/settings/Notifications';
import { SettingsSecurity } from './pages/settings/Security';
import { SettingsLayout } from './pages/settings/SettingsLayout';
import { SettingsWhatsApp } from './pages/settings/WhatsApp';
import { SetupPage } from './pages/Setup';
import { SimulatorPage } from './pages/Simulator';
import { TransactionDetailPage } from './pages/TransactionDetail';
import { TransactionsPage } from './pages/Transactions';
import { TimelinePage } from './pages/Timeline';
import { TpfPage } from './pages/Tpf';
import { TpfDetailPage } from './pages/TpfDetail';
import { ROUTES } from './routes/routes';

// Feature WPs replace one screen at a time — everything else stays a RouteStub until built.
const SCREENS: Partial<Record<string, () => React.JSX.Element>> = {
  '/': DashboardPage,
  '/inbox': InboxPage,
  '/inbox/:id': InboxItemPage,
  '/chat': ChatPage,
  '/accounts': AccountsPage,
  '/accounts/:id': AccountDetailPage,
  '/accounts/:id/reconcile': ReconcilePage,
  '/tx': TransactionsPage,
  '/tx/:id': TransactionDetailPage,
  '/owed': OwedPage,
  '/parties': PartiesPage,
  '/parties/:id': PartyDetailPage,
  '/quotes': QuotationsPage,
  '/quotes/:id': QuotationDetailPage,
  '/projects': ProjectsPage,
  '/projects/:id': ProjectDetailPage,
  '/expected': ExpectedPage,
  '/tpf': TpfPage,
  '/tpf/:id': TpfDetailPage,
  '/import': ImportPage,
  '/recurring': RecurringPage,
  '/search': SearchPage,
  '/health': DataHealthPage,
  '/intel': MoneyIntelligencePage,
  '/explorer': FlowExplorerPage,
  '/agenda': AgendaPage,
  '/availability': AvailabilityPage,
  '/reminders': RemindersPage,
  '/timeline': TimelinePage,
  '/reports': ReportsPage,
  '/reports/:key': ReportDetailPage,
  '/reports/position-over-time': PositionOverTimePage,
  '/goals': GoalsPage,
  '/advisor': AdvisorPage,
  '/simulator': SimulatorPage,
  // '/settings' is a nested layout (see AppRoutes), not a single screen.
};

function FullPageMessage({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex min-h-full items-center justify-center bg-bg text-muted">{children}</div>
  );
}

/** Auth gate for the shell: no owner → /setup; not signed in → /login; not onboarded → /onboarding. */
function RequireAuth({ children }: { children: React.JSX.Element }): React.JSX.Element {
  const location = useLocation();
  const status = useAuthStatus();
  if (status.isLoading) return <FullPageMessage>Loading…</FullPageMessage>;
  if (status.isError) return <FullPageMessage>Offline — can’t reach the server.</FullPageMessage>;
  if (status.data && !status.data.ownerExists) return <Navigate to="/setup" replace />;
  if (status.data && !status.data.authenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (status.data && !status.data.onboarded) return <Navigate to="/onboarding" replace />; // WP-1.19 (OB-1)
  return children;
}

/** S-01: /setup is reachable ONLY while no owner exists; redirect away once provisioned. */
function SetupRoute(): React.JSX.Element {
  const status = useAuthStatus();
  if (status.isLoading) return <FullPageMessage>Loading…</FullPageMessage>;
  if (status.data?.ownerExists === true) return <Navigate to="/" replace />;
  return <SetupPage />;
}

/** S-04: /onboarding requires a session; redirect away once the owner has finished it. */
function OnboardingRoute(): React.JSX.Element {
  const status = useAuthStatus();
  if (status.isLoading) return <FullPageMessage>Loading…</FullPageMessage>;
  if (status.data && !status.data.authenticated) return <Navigate to="/login" replace />;
  if (status.data?.onboarded === true) return <Navigate to="/" replace />;
  return <OnboardingPage />;
}

const standalone = new Set(['/setup', '/login', '/onboarding']);

export function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      {/* standalone */}
      <Route path="/setup" element={<SetupRoute />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />

      {/* shell */}
      <Route
        element={
          <RequireAuth>
            <AppLockProvider>
              <AppShell />
            </AppLockProvider>
          </RequireAuth>
        }
      >
        {ROUTES.filter((r) => !standalone.has(r.path) && r.path !== '/settings').map((r) => {
          const Screen = SCREENS[r.path];
          return (
            <Route
              key={r.path}
              path={r.path}
              element={
                Screen !== undefined ? (
                  <Screen />
                ) : (
                  <RouteStub screenId={r.screenId} title={r.title} />
                )
              }
            />
          );
        })}

        {/* WP-1.20: Settings is a subnav shell with nested sections (S-39..46). */}
        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="financial" replace />} />
          <Route path="financial" element={<SettingsFinancial />} />
          <Route path="categories" element={<SettingsPage />} />
          <Route path="ai" element={<SettingsAi />} />
          <Route path="ai/activity" element={<SettingsAiActivity />} />
          <Route path="whatsapp" element={<SettingsWhatsApp />} />
          <Route path="calendars" element={<SettingsCalendars />} />
          <Route path="notifications" element={<SettingsNotifications />} />
          <Route path="security" element={<SettingsSecurity />} />
          <Route path="backups" element={<SettingsBackups />} />
          <Route path="about" element={<SettingsAbout />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function AppProviders({
  client,
  children,
}: {
  client: QueryClient;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <QueryClientProvider client={client}>
      <ThemeProvider>
        <PrivacyProvider>{children}</PrivacyProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

const queryClient = makeQueryClient();

export function App(): React.JSX.Element {
  return (
    <AppProviders client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AppProviders>
  );
}
